import { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { AnimatePresence } from 'framer-motion';
import ChatOnboardingController from './ChatOnboardingController';
import ChatOnboardingHighlight from './ChatOnboardingHighlight';
import ChatOnboardingStep from './ChatOnboardingStep';
import { useChatOnboardingContext } from './ChatOnboardingController';

// Inner component that uses the context
const ChatOnboardingTourContent = () => {
  const { isActive, currentStepData } = useChatOnboardingContext();

  if (!isActive || !currentStepData) return null;

  return (
    <ChatOnboardingHighlight
      targetSelector={currentStepData.target}
      isActive={isActive}
      padding={12}
      borderRadius={8}
    >
      <ChatOnboardingStep step={currentStepData} />
    </ChatOnboardingHighlight>
  );
};

// Main component that provides the context - using forwardRef for better stability
const ChatOnboardingTour = forwardRef(({ userId, autoStart = false, onComplete, onSkip }, ref) => {
  const [mounted, setMounted] = useState(false);
  const [controller, setController] = useState(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Expose methods through ref
  useImperativeHandle(ref, () => ({
    start: () => {
      if (controller) {
        controller.start();
      }
    },
    reset: () => {
      if (controller) {
        controller.reset();
      }
    }
  }), [controller]);

  if (!mounted) return null;

  return (
    <ChatOnboardingController
      userId={userId}
      onComplete={onComplete}
      onSkip={onSkip}
    >
      <ChatOnboardingTourInner autoStart={autoStart} setController={setController} userId={userId} />
    </ChatOnboardingController>
  );
});

// Inner component to handle auto-start logic
const ChatOnboardingTourInner = ({ autoStart, setController, userId }) => {
  const { startOnboarding, hasCompleted, resetOnboarding } = useChatOnboardingContext();
  const [hasStartedTimer, setHasStartedTimer] = useState(false);
  const timerRef = useRef(null);

  // Expose control methods to parent
  useEffect(() => {
    if (setController) {
      setController({
        start: startOnboarding,
        reset: resetOnboarding,
      });
    }
  }, [setController, startOnboarding, resetOnboarding]);

  useEffect(() => {
    if (!hasStartedTimer && autoStart && !hasCompleted) {
      // Delay start to ensure chat UI is fully rendered
      const timer = setTimeout(() => {
        startOnboarding();
      }, 1000);
      setHasStartedTimer(true);
      return () => clearTimeout(timer);
    }
  }, [autoStart, hasCompleted, startOnboarding, hasStartedTimer]);

  // Auto-start for first-time authenticated users
  useEffect(() => {
    // Skip if we've already started the timer, completed, or autoStart is true
    if (hasStartedTimer || hasCompleted || autoStart) {
      return;
    }

    // Only auto-start if we have a valid user ID (authenticated)
    if (!userId) {
      return;
    }

    // Check if this is the user's first time in chat using user-specific key
    const chatVisitKey = `pickaiChatVisited_${userId}`;
    const existingValue = localStorage.getItem(chatVisitKey);
    const isFirstChatVisit = !existingValue;

    if (isFirstChatVisit) {
      // Prevent multiple timers
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      // Mark that we've started the timer process
      setHasStartedTimer(true);

      // Start onboarding after a delay to ensure UI is ready
      timerRef.current = setTimeout(() => {
        try {
          startOnboarding();
          // Mark as visited ONLY AFTER successfully starting the tour
          localStorage.setItem(chatVisitKey, 'true');
        } catch (error) {
          // Silently handle errors in production
          console.error('[Chat Onboarding] Failed to start:', error);
        }
      }, 1500);
    } else {
      setHasStartedTimer(true);
    }
  }, [hasCompleted, startOnboarding, hasStartedTimer, userId, autoStart]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return <ChatOnboardingTourContent />;
};

ChatOnboardingTour.displayName = 'ChatOnboardingTour';

export default ChatOnboardingTour;